/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.nikam-shreyas.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context";
import { useNavigate, useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import CrudService from "services/cruds-service";

import { useMaterialUIController } from "context";
import MDButton from "components/MDButton";

import InfoBox from "./Infobox";
import MDEditor from "@uiw/react-md-editor";

function ExpandedJob() {
  const { job_id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState({
    _id: job_id,
    recommended_courses: [],
    site: "",
    job_url: "",
    title: "",
    company: "",
    location: "",
    job_type: "",
    date_posted: "",
    interval: "",
    min_amount: "",
    max_amount: "",
    currency: "",
    is_remote: "",
    emails: "",
    description: "",
    company_url: "",
    company_url_direct: "",
    company_addresses: "",
    company_industry: "",
    company_num_employees: "",
    company_revenue: "",
    company_description: "",
    logo_photo_url: "",
    banner_photo_url: "",
    ceo_name: "",
    ceo_photo_url: "",
  });

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);
  const [user, setUser] = useState({});

  useEffect(() => {
    async function checkToken() {
      let user_id = await getCurrentUser();
      if (!user_id) {
        setIsAuthenticated(false);
        localStorage.removeItem("token");
      }
      return user_id;
    }
    checkToken().then((userId) => setUser(userId));
  }, []);

  // function to get a particular job
  useEffect(() => {
    async function fetchJobData() {
      let response = await CrudService.getJobDataById(job_id);
      return response;
    }
    fetchJobData().then((response) => setJob(response));
  }, []);

  const exploreClickHandler = async () => {
    const result = confirm("Would you like to unlock this job?", "Yes", "No");
    if (result) {
      const response = await CrudService.addJobToVisitedJobs({
        job_id: job_id,
        profile_id: user,
      });
      navigate(`/jobs-dashboard/visited-job/${response._id}`);
    }
  };

  // Action buttons for the BookingCard
  const actionButtons = (
    <>
      <Tooltip title="Refresh" placement="bottom">
        <MDTypography
          variant="body1"
          color="primary"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 3 }}
        >
          <Icon color="inherit">refresh</Icon>
        </MDTypography>
      </Tooltip>
      <Tooltip title="Edit" placement="bottom">
        <MDTypography
          variant="body1"
          color="info"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 3 }}
        >
          <Icon color="inherit">edit</Icon>
        </MDTypography>
      </Tooltip>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pr={1}>
        <Grid container spacing={3} sx={{ display: "flex" }}>
          <Grid
            item
            xs={12}
            md={8}
            lg={8}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <MDBox
              key="job_description_block"
              display="block"
              bgColor={darkMode ? "transparent" : "white"}
              color="text"
              borderRadius="xl"
              mt={3}
              py={1.875}
              px={1.875}
              lineHeight={1.5}
              sx={{
                border: ({ borders: { borderWidth }, palette: { white } }) =>
                  darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                fontSize: ({ typography: { size } }) => size.md,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={3} lg={1}>
                  {job.logo_photo_url !== null &&
                  job.logo_photo_url != "" &&
                  job.logo_photo_url != "NaN" ? (
                    <MDAvatar
                      src={job.logo_photo_url}
                      alt="profile-image"
                      size="xl"
                      shadow=""
                    />
                  ) : (
                    <MDAvatar
                      src="https://assets.procurement.opengov.com/assets/unknown-business-logo.png"
                      alt="profile-image"
                      size="xl"
                      shadow=""
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  <MDTypography variant="h5" mt={1}>
                    {job.title ? job.title : ""}
                  </MDTypography>
                  <MDTypography variant="h6" mt={1}>
                    {job.company ? job.company : ""}
                  </MDTypography>
                </Grid>

                {/* <Grid item xs={12} md={2} lg={2}>
                  {job.job_url !== null && job.job_url != "" && job.job_url != "NaN" ? (
                    <MDButton
                      variant="outlined"
                      color="info"
                      fullwidth
                      onClick={() => window.open(job.job_url, "_blank")}
                    >
                      Apply
                    </MDButton>
                  ) : null}
                </Grid> */}
                <Grid item xs={12} md={3} lg={3}>
                  {/* <MDTypography variant="caption" mt={1}>
                    Explore how we can help you get this job
                  </MDTypography> */}
                  <MDButton
                    variant="gradient"
                    color="info"
                    style={{
                      // float right
                      float: "right",
                    }}
                    onClick={exploreClickHandler}
                  >
                    Job Boost
                  </MDButton>
                </Grid>
              </Grid>

              <MDTypography variant="h6" color="text" mt={1}>
                {"Job Description"}
              </MDTypography>
              <MDBox m={1} p={3} style={{ height: "65vh", overflowY: "auto" }}>
                <div data-color-mode="light">
                  <MDEditor.Markdown
                    source={job.description}
                    style={{ color: "#344767", background: "transparent" }}
                    components={{
                      p: ({ node, ...props }) => (
                        <p style={{ color: "#7b809a" }} {...props} />
                      ),
                    }}
                  />
                </div>
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={4} lg={4}>
            <MDBox style={{ height: "65vh", overflowY: "auto" }}>
              {/* Job Location */}
              <InfoBox
                title="Location"
                content={job.location}
                darkMode={darkMode}
              />

              {/* CEO Information */}
              {job.ceo_name !== null &&
              job.ceo_name != "" &&
              job.ceo_name != "NaN" &&
              job.ceo_photo_url !== null &&
              job.ceo_photo_url != "" &&
              job.ceo_photo_url != "NaN" ? (
                <MDBox
                  key="ceo_block"
                  display="block"
                  bgColor={darkMode ? "transparent" : "white"}
                  color="text"
                  borderRadius="xl"
                  mt={2.5}
                  py={1.875}
                  px={1.875}
                  lineHeight={1.5}
                  sx={{
                    border: ({
                      borders: { borderWidth },
                      palette: { white },
                    }) =>
                      darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                    fontSize: ({ typography: { size } }) => size.md,
                  }}
                >
                  <MDTypography variant="h6" fontWeight="medium" mt={1}>
                    {"CEO"}
                  </MDTypography>
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <MDBox mb={1}>
                      <MDAvatar
                        src={job.ceo_photo_url}
                        alt="profile-image"
                        size="xl"
                        shadow="sm"
                      />
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="medium" mt={1}>
                      {job.ceo_name}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ) : null}
              {/* Company Address Information*/}
              <InfoBox
                title="Company Address"
                content={job.company_addresses}
                darkMode={darkMode}
              />

              {/* Company Industry information */}
              <InfoBox
                title="Company Industry information"
                content={job.company_industry}
                darkMode={darkMode}
              />
              {/* Company Revenue Information */}
              <InfoBox
                title="Company Revenue Information"
                content={job.company_revenue}
                darkMode={darkMode}
              />
              {/* Number of employees information */}
              <InfoBox
                title="Number of Employees"
                content={job.company_num_employees}
                darkMode={darkMode}
              />

              {/* Salary Information */}
              {job.min_amount !== null &&
              job.max_amount !== null &&
              job.currency !== null &&
              job.min_amount != "" &&
              job.min_amount != "NaN" &&
              job.max_amount != "" &&
              job.max_amount != "NaN" &&
              job.currency != "" &&
              job.currency != "NaN" ? (
                <MDBox
                  key="salary_block"
                  display="block"
                  bgColor={darkMode ? "transparent" : "white"}
                  color="text"
                  borderRadius="xl"
                  mt={2.5}
                  py={1.875}
                  px={1.875}
                  lineHeight={1.5}
                  sx={{
                    border: ({
                      borders: { borderWidth },
                      palette: { white },
                    }) =>
                      darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                    fontSize: ({ typography: { size } }) => size.md,
                  }}
                >
                  <MDTypography variant="h6" fontWeight="medium" mt={1}>
                    {"Salary"}
                  </MDTypography>
                  {job.min_amount + " - " + job.max_amount + " " + job.currency}
                </MDBox>
              ) : null}

              {/* Job Type Information */}
              <InfoBox
                title="Job Type"
                content={job.job_type}
                darkMode={darkMode}
              />
              {/* Job Remote Type Information*/}
              <InfoBox
                title="Remote"
                content={job.is_remote}
                darkMode={darkMode}
              />
              {/* Job Site Inforamtion */}
              <InfoBox
                title="Job Site Inforamtion"
                content={job.site}
                darkMode={darkMode}
              />
              {/* Company Description*/}
              <InfoBox
                title="Company Description"
                content={job.company_description}
                darkMode={darkMode}
              />

              {/* Company URL */}
              <InfoBox
                title="Company URL"
                content={job.company_url}
                darkMode={darkMode}
              />

              {/* Company URL Direct */}
              <InfoBox
                title="Company URL Direct"
                content={job.company_url_direct}
                darkMode={darkMode}
              />
              {/* Emails */}
              <InfoBox
                title="Emails"
                content={job.emails}
                darkMode={darkMode}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ExpandedJob;
